import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Menu,
  X,
  CheckCircle,
  Mail,
  Phone,
  MapPin,
  Shield,
  Users,
  FileText,
  Send,
  DollarSign,
  Headphones,
  BarChart3,
  Calculator,
} from 'lucide-react';
import { Wordmark, Eyebrow, CtaChevrons, ConsultationForm } from './shared';

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  React.useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = id => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const services = [
    {
      icon: Shield,
      title: 'Credentialing',
      description: 'Credentialing issues are addressed before they impact revenue.',
      details: [
        'Complete CAQH profile management and maintenance',
        'Primary source verification for all credentials',
        'Insurance network enrollment and contracting',
        'Re-credentialing and ongoing monitoring',
        'Expedited processing to minimize revenue delays',
        'Credentialing status tracking and reporting',
      ],
    },
    {
      icon: Users,
      title: 'Patient Data Collection',
      description: 'We flag gaps before they become denials.',
      details: [
        'Comprehensive demographic data collection',
        'Insurance verification and eligibility checks',
        'Prior authorization management',
        'Medical necessity documentation',
        'Intelligent data mapping to billing systems',
        'Quality assurance checks to reduce errors',
      ],
    },
    {
      icon: FileText,
      title: 'Diagnostic & Procedure Coding',
      description: 'ICD-10 and CPT coding by certified pathology specialists.',
      details: [
        'ICD-10 and CPT coding',
        'Pathology-specific coding expertise',
        'Regular coding audits for compliance',
        'Stay current with coding updates and changes',
        'Modifier application for maximum reimbursement',
        'Documentation review and improvement recommendations',
      ],
    },
    {
      icon: Send,
      title: 'Insurance Submission',
      description: 'Electronic claim submission for timely filings and reduced rejections.',
      details: [
        'Electronic claims submission in 837 file format',
        'Real-time claim scrubbing and error detection',
        'Clearinghouse management and reconciliation',
        'Timely filing monitoring and alerts',
        'Claim status tracking and follow-up',
        'Reduced claim rejections through validation',
      ],
    },
    {
      icon: Headphones,
      title: 'Patient Billing & Management',
      description: 'Pathology aware messaging to ensure patients feel informed.',
      details: [
        'Professional call center with trained billing specialists',
        'Multi-channel communication (phone, mail, email, text)',
        'Patient payment plan setup and management',
        'Clear, easy-to-understand patient statements',
        'Online payment portal integration',
        'Compassionate collections approach with high recovery rates',
      ],
    },
    {
      icon: DollarSign,
      title: 'Reimbursement Calculations',
      description: 'Reimbursement analysis, contract rate optimization, and payment variance reporting.',
      details: [
        'Accurate expected reimbursement calculations',
        'Payer-specific fee schedule management',
        'Contract rate analysis and optimization',
        'Denial pattern identification and resolution',
        'Payment variance analysis and reporting',
        'Revenue cycle performance metrics',
      ],
    },
    {
      icon: BarChart3,
      title: 'Data Analytics & Denial Management',
      description: 'Live dashboard, denial management automation, and custom reporting tailored to your needs.',
      details: [
        'Real-time dashboard with key performance indicators',
        'Payer-specific claim requirement tracking',
        'Proactive claim adjustments before submission',
        'Denial management and appeal automation',
        'Benchmarking against industry standards',
        'Custom reporting tailored to your needs',
      ],
    },
  ];

  const whyUsCards = [
    {
      icon: DollarSign,
      title: 'We Chase Every Dollar',
      body: 'Every path that can be taken to retrieve revenue will be taken. We work every claim with AI assisted workflows, chase every denial, and follow every dollar until your reimbursement is fully realized.',
    },
    {
      icon: Users,
      title: 'Dedicated Account Team',
      body: 'Your assigned team learns your specific workflows, payer mix, and preferences for consistent expert support.',
    },
    {
      icon: Shield,
      title: 'Pathology-Exclusive',
      body: "We don't dabble in pathology. Every system, every hire, and every process was built exclusively for pathology billing. This means fewer denials, faster reimbursements, and a team that speaks your language.",
    },
    {
      icon: BarChart3,
      title: 'Full Accountability',
      body: "Data and analytics give you the power to keep us accountable without taking up all of your time. With a live dashboard and real time reporting, there's no more waiting for monthly reports to evaluate performance.",
    },
    {
      icon: MapPin,
      title: '100% US-Based',
      body: 'We take pride that data never leaves US soil. This guarantees HIPAA compliance and seamless communication during your business hours.',
    },
  ];

  const stats = [
    { val: 'Competitive Pricing', lbl: 'Full-service billing · no hidden fees' },
    { val: '> 92%', lbl: 'Average 12-month net collection rate' },
    { val: '100%', lbl: 'US-based team & operations' },
    { val: '24/7', lbl: 'Claims monitoring & support' },
    { val: '0', lbl: 'Hidden fees or start up costs' },
  ];

  const navItems = [
    { id: 'home', label: 'Home' },
    { id: 'why-us', label: 'Why Us' },
    { id: 'services', label: 'Services' },
    { id: 'contact', label: 'Contact' },
  ];

  const navRouteItems = [
    { to: '/case-studies', label: 'Case Studies' },
  ];

  return (
    <div className='min-h-screen bg-brand-cream text-brand-charcoal font-body'>
      {/* Navigation */}
      <nav
        className={`fixed w-full z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-brand-warmwhite/95 backdrop-blur-sm shadow-md border-b border-brand-coffee/10'
            : 'bg-transparent'
        }`}
      >
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center h-20'>
            <button onClick={() => scrollToSection('home')} className='text-left'>
              <Wordmark />
            </button>
            <div className='hidden md:flex items-center space-x-10'>
              {navItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className='font-body text-sm font-medium uppercase text-brand-coffee hover:text-brand-ember transition-colors'
                  style={{ letterSpacing: '0.18em' }}
                >
                  {item.label}
                </button>
              ))}
              {navRouteItems.map(item => (
                <Link
                  key={item.to}
                  to={item.to}
                  className='font-body text-sm font-medium uppercase text-brand-coffee hover:text-brand-ember transition-colors'
                  style={{ letterSpacing: '0.18em' }}
                >
                  {item.label}
                </Link>
              ))}
            </div>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className='md:hidden text-brand-coffee'
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
        {isMenuOpen && (
          <div className='md:hidden bg-brand-warmwhite border-b border-brand-coffee/10 shadow-md'>
            <div className='px-4 pt-2 pb-6 space-y-2'>
              {navItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className='block w-full text-left py-2 font-body text-sm font-medium uppercase text-brand-coffee hover:text-brand-ember transition-colors'
                  style={{ letterSpacing: '0.18em' }}
                >
                  {item.label}
                </button>
              ))}
              {navRouteItems.map(item => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setIsMenuOpen(false)}
                  className='block py-2 font-body text-sm font-medium uppercase text-brand-coffee hover:text-brand-ember transition-colors'
                  style={{ letterSpacing: '0.18em' }}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section id='home' className='relative min-h-screen flex items-center bg-brand-cream scroll-mt-20'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-32 pb-32'>
          <div className='grid grid-cols-1 lg:grid-cols-12 gap-12 items-center'>
            <div className='lg:col-span-7'>
              <Eyebrow className='text-brand-ember mb-6'>Exclusive Pathology Billing</Eyebrow>
              <h1 className='font-display font-bold text-brand-coffee text-5xl sm:text-6xl md:text-7xl leading-[1.05] mb-8'>
                Dawning a{' '}
                <em className='text-brand-ember'>New&nbsp;Era</em>
                <br />
                in Pathology RCM
              </h1>
              <p className='font-body text-lg sm:text-xl text-brand-stone leading-relaxed max-w-2xl mb-10'>
                Specialized revenue cycle management for pathology laboratories — from
                molecular diagnostics to anatomic pathology. With complete transparency
                and a team that will chase every dollar, it's time you expected more
                from your revenue.
              </p>
              <div className='inline-flex items-center mt-[10px]'>
                <CtaChevrons side='left' />
                <Link
                  to='/calculator'
                  className='inline-flex items-center gap-2 bg-brand-ember hover:bg-brand-coffee text-brand-warmwhite font-body font-semibold text-sm uppercase py-4 px-10 transition-colors duration-300'
                  style={{ letterSpacing: '0.18em' }}
                >
                  <Calculator size={16} />
                  Try the Revenue Calculator
                </Link>
                <CtaChevrons side='right' />
              </div>
            </div>
            <div className='hidden lg:flex lg:col-span-5 justify-center items-center h-96' aria-hidden='true'>
              <div className='rounded-xl border-2 border-brand-ember overflow-hidden w-full'>
                <img
                  src='/sunrise_abstract_horizon_final.svg'
                  alt='Horizon RCM — exclusive pathology billing and revenue cycle management'
                  className='block w-full h-auto'
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section id='why-us' className='bg-brand-navy py-24 scroll-mt-20'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='text-center max-w-3xl mx-auto mb-16'>
            <Eyebrow className='text-brand-ember mb-4'>Why Horizon RCM</Eyebrow>
            <h2 className='font-display font-bold text-brand-warmwhite text-4xl sm:text-5xl mb-6'>
              Why Choose Horizon RCM?
            </h2>
            <p className='font-body text-lg text-brand-cream/70 leading-relaxed'>
              We're not just another RCM company. We're exclusive pathology billing
              specialists — 100% US-based — committed to maximizing your revenue.
            </p>
          </div>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-brand-cream/15 border border-brand-cream/15 mb-20'>
            {whyUsCards.map((card, i) => {
              const Icon = card.icon;
              return (
                <div
                  key={i}
                  className='bg-brand-navy p-8 sm:p-10 hover:bg-brand-charcoal transition-colors duration-300'
                >
                  <Icon size={28} className='text-brand-ember mb-6' />
                  <h3 className='font-display font-bold text-brand-warmwhite text-2xl mb-4'>
                    {card.title}
                  </h3>
                  <p className='font-body text-brand-cream/70 leading-relaxed'>
                    {card.body}
                  </p>
                </div>
              );
            })}
            <div className='hidden lg:flex bg-brand-navy p-8 sm:p-10 items-end'>
              <Wordmark onDark />
            </div>
          </div>
          <div className='grid grid-cols-2 md:grid-cols-5 gap-y-12 gap-x-6'>
            {stats.map((stat, i) => (
              <div key={i} className='text-center'>
                <div className='font-display font-bold text-brand-ember text-4xl sm:text-5xl mb-3'>
                  {stat.val}
                </div>
                <div
                  className='font-body text-[10px] font-semibold uppercase text-brand-cream/60 leading-relaxed max-w-[10rem] mx-auto'
                  style={{ letterSpacing: '0.18em' }}
                >
                  {stat.lbl}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id='services' className='bg-brand-warmwhite py-24 scroll-mt-20'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='max-w-3xl mb-20'>
            <Eyebrow className='text-brand-ember mb-4'>Our Services</Eyebrow>
            <h2 className='font-display font-bold text-brand-coffee text-4xl sm:text-5xl mb-6'>
              End-to-End Pathology Billing
            </h2>
            <p className='font-body text-lg text-brand-stone leading-relaxed'>
              Comprehensive pathology revenue cycle management — credentialing,
              coding, claims, patient billing, and denial management — designed
              exclusively for pathology laboratories
            </p>
          </div>
          <div className='space-y-0'>
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className='border-t border-brand-coffee/15 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10'
                >
                  <div className='lg:col-span-5'>
                    <div className='flex items-baseline gap-5 mb-6'>
                      <span className='font-display font-bold text-brand-ember text-5xl leading-none'>
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <Icon size={28} className='text-brand-coffee self-center' />
                    </div>
                    <h3 className='font-display font-bold text-brand-coffee text-3xl mb-4'>
                      {service.title}
                    </h3>
                    <p className='font-body text-lg text-brand-stone leading-relaxed mb-8 max-w-md'>
                      {service.description}
                    </p>
                    <Eyebrow className='text-brand-stone/80'>
                      Service {index + 1} of {services.length}
                    </Eyebrow>
                  </div>
                  <div className='lg:col-span-7'>
                    <div className='bg-brand-cream border border-brand-coffee/10 p-8 sm:p-10 h-full'>
                      <h4 className='font-display font-bold text-brand-coffee text-xl mb-6'>
                        What We Deliver
                      </h4>
                      <ul className='space-y-4'>
                        {service.details.map((detail, idx) => (
                          <li key={idx} className='flex items-start'>
                            <CheckCircle
                              className='text-brand-ember mr-3 flex-shrink-0 mt-0.5'
                              size={18}
                            />
                            <span className='font-body text-brand-charcoal/90 leading-relaxed'>
                              {detail}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id='contact' className='bg-brand-cream py-24 scroll-mt-20'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='text-center max-w-3xl mx-auto mb-16'>
            <h2 className='font-display font-bold text-brand-coffee text-4xl sm:text-5xl mb-6'>
              Ready to Transform Your Revenue Cycle?
            </h2>
            <p className='font-body text-lg text-brand-stone leading-relaxed'>
              Contact us today to learn how Horizon RCM can optimize your
              pathology billing and increase your revenue
            </p>
          </div>
          <div className='grid grid-cols-1 lg:grid-cols-12 gap-12'>
            <div className='lg:col-span-4 space-y-10'>
              <div>
                <Eyebrow className='text-brand-stone/80 mb-2'>Email</Eyebrow>
                <div className='flex items-center gap-3'>
                  <Mail className='text-brand-ember flex-shrink-0' size={20} />
                  <a
                    href='mailto:main@horizon-rcm.com'
                    className='font-display text-xl text-brand-coffee hover:text-brand-ember transition-colors break-all'
                  >
                    main@horizon-rcm.com
                  </a>
                </div>
              </div>
              <div>
                <Eyebrow className='text-brand-stone/80 mb-2'>Phone</Eyebrow>
                <div className='flex items-center gap-3'>
                  <Phone className='text-brand-ember flex-shrink-0' size={20} />
                  <a
                    href='tel:+18882576123'
                    className='font-display text-xl text-brand-coffee hover:text-brand-ember transition-colors'
                  >
                    (888) 257-6123
                  </a>
                </div>
              </div>
              <div>
                <Eyebrow className='text-brand-stone/80 mb-2'>Location</Eyebrow>
                <div className='flex items-center gap-3'>
                  <MapPin className='text-brand-ember flex-shrink-0' size={20} />
                  <p className='font-display text-xl text-brand-coffee'>United States</p>
                </div>
              </div>
              <div className='h-1 w-24 bg-brand-ember'></div>
            </div>
            <div className='lg:col-span-8'>
              <div className='bg-brand-warmwhite border border-brand-coffee/10 p-8 sm:p-12 shadow-sm'>
                <ConsultationForm idPrefix='home' />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className='bg-brand-navy py-12 border-t-4 border-brand-ember'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center gap-6'>
          <Wordmark onDark className='text-center' />
          <div className='text-center'>
            <p className='font-body text-brand-cream/60 text-sm'>
              © 2025 Horizon RCM Partners LLC. All rights reserved.
            </p>
            <p
              className='font-body text-brand-cream/40 mt-2 text-[10px] font-semibold uppercase'
              style={{ letterSpacing: '0.28em' }}
            >
              Exclusive Pathology Billing · Pathology Revenue Cycle Management · 100% US-Based
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;